import { absUrl } from "@/lib/url";
import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { HijriMonthGrid } from "@/components/calendar/HijriMonthGrid";
import { HijriEventBadge } from "@/components/calendar/HijriEventBadge";
import { PdfExportButton } from "@/components/PdfExportButton";
import { AdSlot } from "@/components/AdSlot";
import {
  HIJRI_MONTHS,
  HIJRI_MONTHS_EN,
  hijriToGregorian,
  daysInHijriMonth,
} from "@/lib/calendar/hijri";
import { getHijriEventsForMonth } from "@/lib/calendar/hijri-events";
import { MONTH_NAMES_ID, calendarDatePath } from "@/lib/calendar/dates";
import { getHolidaysForDate } from "@/data/holidays";
import { useLocale } from "@/i18n/LocaleContext";
import { translateHolidayName } from "@/i18n/holidays";
import type { Locale } from "@/i18n/types";
import { LongWeekendTable } from "@/components/pages/PseoExtras";
import { getHolidaysForYear } from "@/data/holidays";
import { hijriMonthEntities, prayerEntities } from "@/lib/seo/entities";
import { datasetSchema } from "@/lib/seo/schema";

export function HijriMonthPage({ hYear, hMonth, locale }: { hYear: number; hMonth: number; locale?: Locale }) {
  return (
    <SiteShell locale={locale}>
      <Body hYear={hYear} hMonth={hMonth} />
    </SiteShell>
  );
}

function Body({ hYear, hMonth }: { hYear: number; hMonth: number }) {
  const { t, href, locale } = useLocale();
  const monthName = (locale === "en" ? HIJRI_MONTHS_EN : HIJRI_MONTHS)[hMonth - 1];
  const total = daysInHijriMonth(hYear, hMonth);
  const first = hijriToGregorian(hYear, hMonth, 1);
  const last = hijriToGregorian(hYear, hMonth, total);
  const gFirst = `${first.d} ${t.common.months[first.m - 1]} ${first.y}`;
  const gLast = `${last.d} ${t.common.months[last.m - 1]} ${last.y}`;
  const important = getHijriEventsForMonth(hMonth);

  // Build list of Indonesian holidays falling within this Hijri month.
  const liburInHijri: Array<{ hDay: number; gY: number; gM: number; gD: number; name: string; type: "libur" | "cuti-bersama" }> = [];
  for (let d = 1; d <= total; d++) {
    const g = hijriToGregorian(hYear, hMonth, d);
    const iso = `${g.y}-${String(g.m).padStart(2, "0")}-${String(g.d).padStart(2, "0")}`;
    for (const h of getHolidaysForDate(iso)) {
      liburInHijri.push({ hDay: d, gY: g.y, gM: g.m, gD: g.d, name: h.name, type: h.type });
    }
  }
  const liburRows = liburInHijri.filter((x) => x.type === "libur");
  const cutiRows = liburInHijri.filter((x) => x.type === "cuti-bersama");

  const prev = hMonth === 1 ? { y: hYear - 1, m: 12 } : { y: hYear, m: hMonth - 1 };
  const next = hMonth === 12 ? { y: hYear + 1, m: 1 } : { y: hYear, m: hMonth + 1 };

  const ld = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t.common.home, item: absUrl(href("/")) },
      { "@type": "ListItem", position: 2, name: t.common.hijriCalendar, item: absUrl(href("/kalender-hijriah/")) },
      { "@type": "ListItem", position: 3, name: `${hYear} H`, item: absUrl(href(`/kalender-hijriah/${hYear}/`)) },
      { "@type": "ListItem", position: 4, name: `${monthName} ${hYear} H`, item: absUrl(href(`/kalender-hijriah/${hYear}/${hMonth}/`)) },
    ],
  };
  const monthEntity = hijriMonthEntities()[hMonth - 1];
  const datasetLd = datasetSchema({
    name: `${monthName} ${hYear} H`,
    description: `Bulan ${monthName} ${hYear} H bertepatan ${gFirst} - ${gLast}. Termasuk peristiwa Islam dan padanan tanggal Masehi.`,
    url: `/kalender-hijriah/${hYear}/${hMonth}/`,
    locale: locale === "en" ? "en" : "id",
    temporalCoverage: `${first.y}-${String(first.m).padStart(2, "0")}-${String(first.d).padStart(2, "0")}/${last.y}-${String(last.m).padStart(2, "0")}-${String(last.d).padStart(2, "0")}`,
    about: [monthEntity, ...(hMonth === 9 ? prayerEntities() : [])],
  });

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(datasetLd) }} />
      <nav className="py-4 text-sm text-muted-foreground">
        <Link to={href("/") as never} className="hover:text-primary">{t.common.home}</Link>
        {" / "}
        <Link to={href("/kalender-hijriah/") as never} className="hover:text-primary">{t.common.hijriCalendar}</Link>
        {" / "}
        <Link to={href(`/kalender-hijriah/${hYear}/`) as never} className="hover:text-primary">{hYear} H</Link>
        {" / "}{monthName} {hYear} H
      </nav>

      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t.common.calendar} {monthName} {hYear} {locale === "en" ? "AH" : "H"}
          </h1>
          <p className="mt-1 text-muted-foreground">
            {gFirst} - {gLast} ({total} {locale === "en" ? "days" : "hari"})
          </p>
        </div>
        <PdfExportButton mode="h-month" year={first.y} hYear={hYear} hMonth={hMonth} allowSwitch />
      </header>

      <div className="space-y-6">
        <div className="mx-auto w-full max-w-5xl space-y-4">
          <HijriMonthGrid hYear={hYear} hMonth={hMonth} />
          <div className="flex justify-between text-sm">
            <Link
              to={href(`/kalender-hijriah/${prev.y}/${prev.m}/`) as never}
              className="rounded-md border border-border px-3 py-1.5 hover:bg-accent"
            >
              ← {(locale === "en" ? HIJRI_MONTHS_EN : HIJRI_MONTHS)[prev.m - 1]} {prev.y} H
            </Link>
            <Link
              to={href(`/kalender-hijriah/${next.y}/${next.m}/`) as never}
              className="rounded-md border border-border px-3 py-1.5 hover:bg-accent"
            >
              {(locale === "en" ? HIJRI_MONTHS_EN : HIJRI_MONTHS)[next.m - 1]} {next.y} H →
            </Link>
          </div>

          <div className="rounded-lg border border-border bg-card p-3">
            <p className="mb-2 text-xs font-semibold text-muted-foreground">
              {locale === "en" ? "Other months" : "Bulan lain"} {hYear} H
            </p>
            <div className="flex flex-wrap gap-1.5">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((mi) => {
                const nm = (locale === "en" ? HIJRI_MONTHS_EN : HIJRI_MONTHS)[mi - 1];
                return (
                  <Link
                    key={mi}
                    to={href(`/kalender-hijriah/${hYear}/${mi}/`) as never}
                    className={
                      "rounded-md border border-border px-2 py-0.5 text-xs hover:bg-accent " +
                      (mi === hMonth ? "bg-accent font-semibold" : "")
                    }
                  >
                    {nm.slice(0, 5)}
                  </Link>
                );
              })}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <Link
                to={href(`/${MONTH_NAMES_ID[first.m - 1].toLowerCase()}-${first.y}`) as never}
                className="text-primary hover:underline"
              >
                {t.common.viewInGregorian}: {t.common.months[first.m - 1]} {first.y}
              </Link>
            </div>
          </div>

          <p className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            {locale === "en"
              ? "Hijri dates use the arithmetic civil (Kuwaiti) calendar and may differ by one day from moon-sighting (rukyatul hilal). Official Indonesian dates for Ramadan, Eid, and Dhul-Hijjah follow the Kemenag isbat session."
              : "Tanggal Hijriah menggunakan algoritma aritmetika sipil (Kuwaiti) dan dapat berbeda 1 hari dari rukyatul hilal. Penetapan resmi awal Ramadhan, Idul Fitri, dan Idul Adha di Indonesia mengikuti sidang isbat Kemenag."}
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {important.length > 0 && (
            <div className="rounded-lg border border-border bg-card p-4">
              <h2 className="mb-2 font-semibold">{t.common.importantIslamicDays}</h2>
              <ul className="space-y-1.5 text-sm">
                {important.map((it) => {
                  const g = hijriToGregorian(hYear, hMonth, it.hDay);
                  const evName = locale === "en" ? it.nameEn : it.name;
                  const evDesc = locale === "en" ? it.descEn : it.descId;
                  const dotClass = it.type === "hari-besar" ? "bg-emerald-500" : it.type === "puasa" ? "bg-amber-500" : "bg-muted-foreground";
                  return (
                    <li key={`${it.hDay}-${it.name}`} className="flex items-start gap-2">
                      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dotClass}`} />
                      <div className="min-w-0">
                        <HijriEventBadge event={it} hYear={hYear} className="text-left hover:text-primary">
                          <span className="font-medium">{it.hDay} {monthName}</span>
                          <span className="text-muted-foreground"> - {evName}</span>
                        </HijriEventBadge>
                        <div className="text-xs text-muted-foreground">{g.d} {t.common.months[g.m - 1]} {g.y}</div>
                        <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{evDesc}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="rounded-lg border border-border bg-card p-4">
            <h2 className="mb-2 font-semibold">{t.common.nationalHolidays} ({monthName})</h2>
            {liburRows.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {locale === "en" ? "No national holiday this month." : "Tidak ada libur nasional bulan ini."}
              </p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {liburRows.map((h, i) => (
                  <li key={i}>
                    <Link
                      to={href(calendarDatePath(h.gY, h.gM, h.gD)) as never}
                      className="hover:text-primary"
                    >
                      <span className="font-medium text-red-date">{h.hDay} {monthName}</span>
                      <span className="text-muted-foreground"> ({h.gD} {t.common.months[h.gM - 1]})</span>
                      <span> - {translateHolidayName(h.name, locale)}</span>
                    </Link>
                  </li>
                ))}
                {cutiRows.map((h, i) => (
                  <li key={`c${i}`}>
                    <Link
                      to={href(calendarDatePath(h.gY, h.gM, h.gD)) as never}
                      className="hover:text-primary"
                    >
                      <span className="font-medium text-cuti-bersama">{h.hDay} {monthName}</span>
                      <span className="text-muted-foreground"> ({h.gD} {t.common.months[h.gM - 1]})</span>
                      <span> - {translateHolidayName(h.name, locale)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3 text-xs">
              <Link
                to={href(`/${MONTH_NAMES_ID[first.m - 1].toLowerCase()}-${first.y}`) as never}
                className="text-primary hover:underline"
              >
                {t.common.viewInGregorian}: {t.common.months[first.m - 1]} {first.y}
              </Link>
              {(first.m !== last.m || first.y !== last.y) && (
                <>
                  {" • "}
                  <Link
                    to={href(`/${MONTH_NAMES_ID[last.m - 1].toLowerCase()}-${last.y}`) as never}
                    className="text-primary hover:underline"
                  >
                    {t.common.months[last.m - 1]} {last.y}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
        <AdSlot size="rectangle" position="sidebar" />
      </div>

      <div className="mt-6 space-y-6">
        {Array.from(new Set([first.y, last.y])).map((gy) => (
          <LongWeekendTable
            key={gy}
            year={gy}
            holidays={getHolidaysForYear(gy)}
            filterRange={{
              startISO: `${first.y}-${String(first.m).padStart(2, "0")}-${String(first.d).padStart(2, "0")}`,
              endISO: `${last.y}-${String(last.m).padStart(2, "0")}-${String(last.d).padStart(2, "0")}`,
            }}
            heading={locale === "en"
              ? `Long weekend & harpitnas in ${monthName} ${hYear} AH`
              : `Long weekend & harpitnas di ${monthName} ${hYear} H`}
          />
        ))}
      </div>
    </>
  );
}